import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { LucideIconsModule } from '../../lucide-icons.module';
import {
    MEDIA_LIBRARY_DIALOG_MESSAGE_TYPE,
    MEDIA_LIBRARY_POPUP_BLOCKED_EVENT,
    MediaLibraryPopupBlockedDetail
} from '../../utils/media-library-popup.util';

type MediaLibraryDialogState = {
    open: boolean;
    popupUrl: string | null;
    title: string;
};

@Component({
    selector: 'app-media-library-dialog',
    standalone: true,
    imports: [CommonModule, LucideIconsModule],
    templateUrl: './media-library-dialog.component.html',
    styleUrls: ['./media-library-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaLibraryDialogComponent implements OnInit, OnDestroy {
    private readonly doc = inject(DOCUMENT);
    private readonly cdr = inject(ChangeDetectorRef);
    private previousBodyOverflow: string | null = null;

    state: MediaLibraryDialogState = {
        open: false,
        popupUrl: null,
        title: 'Media library'
    };

    private readonly handlePopupBlockedEvent = (event: Event): void => {
        const customEvent = event as CustomEvent<MediaLibraryPopupBlockedDetail>;
        const detail = customEvent.detail;
        if (!detail?.url) return;
        this.open(detail);
    };

    ngOnInit(): void {
        if (typeof window === 'undefined') return;
        window.addEventListener(MEDIA_LIBRARY_POPUP_BLOCKED_EVENT, this.handlePopupBlockedEvent as EventListener);
    }

    ngOnDestroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener(MEDIA_LIBRARY_POPUP_BLOCKED_EVENT, this.handlePopupBlockedEvent as EventListener);
        }
        this.unlockScroll();
    }

    @HostListener('window:message', ['$event'])
    onWindowMessage(event: MessageEvent): void {
        if (typeof window === 'undefined') return;
        if (event.origin !== window.location.origin) return;
        const data = event.data;
        if (!data || typeof data !== 'object') return;
        if (String((data as any).type || '').trim() !== MEDIA_LIBRARY_DIALOG_MESSAGE_TYPE) return;

        const action = String((data as any).action || '').trim().toLowerCase();
        if (action === 'assigned') {
            window.dispatchEvent(new CustomEvent('nibni:media-library-assigned', { detail: data }));
            this.close();
            return;
        }
        if (action === 'close') {
            this.close();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.state.open) this.close();
    }

    close(event?: Event): void {
        event?.stopPropagation();
        this.unlockScroll();
        this.state = {
            open: false,
            popupUrl: null,
            title: 'Media library'
        };
        this.cdr.markForCheck();
    }

    retryPopup(event?: Event): void {
        event?.stopPropagation();
        const url = String(this.state.popupUrl || '').trim();
        if (!url || typeof window === 'undefined') return;
        const popup = window.open(url, '_blank', 'popup=yes,resizable=yes,scrollbars=yes,width=1320,height=880');
        if (popup) {
            popup.focus();
            this.close();
        }
    }

    private open(detail: MediaLibraryPopupBlockedDetail): void {
        this.lockScroll();
        this.state = {
            open: true,
            popupUrl: detail.url,
            title: this.resolveTitle(detail)
        };
        this.cdr.markForCheck();
    }

    private resolveTitle(detail: MediaLibraryPopupBlockedDetail): string {
        const targetType = String(detail?.options?.targetType || '').trim().toLowerCase();
        if (targetType === 'property') return 'Choose media for unit';
        if (targetType === 'project') return 'Choose media for project';
        if (targetType === 'phase') return 'Choose media for phase';
        if (targetType === 'org') return 'Choose organization media';
        return 'Media library';
    }

    private lockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow !== null) return;
        this.previousBodyOverflow = this.doc.body.style.overflow || '';
        this.doc.body.style.overflow = 'hidden';
    }

    private unlockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow === null) return;
        this.doc.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
    }
}
